import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Lock, Loader2 } from "lucide-react";

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_protocol-hub-13/artifacts/6oqjboke_Splendor%20Logo%202020.jpg";
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_e09a3d89-e198-49a6-8deb-cc014425060d/artifacts/p6k94g9a_OK.jpeg";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showMessage("Inserisci email e password", "error");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (error) {
      showMessage(error.response?.data?.detail || "Credenziali non valide", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-black/40" />
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      <div className="w-full max-w-md animate-fade-in relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src={LOGO_MAIN}
            alt="Splendor Sud S.r.l."
            className="h-24 object-contain"
          />
        </div>

        <Card className="bg-white border-border shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-heading font-bold text-foreground">
              Registro Protocollo
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Gestione Protocollo Aziendale
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder=""
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-[#2D7BB7]"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    id="password"
                    type="password"
                    placeholder=""
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-[#2D7BB7]"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white font-medium py-2.5 shadow-md hover:shadow-lg transition-all duration-300"
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accesso in corso...
                  </>
                ) : (
                  "Accedi"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#2D7BB7] hover:underline"
              >
                Password dimenticata?
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black/50" 
              onClick={() => setShowForgotPassword(false)}
            />
            <Card className="relative z-50 bg-white border-border shadow-xl w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-xl font-heading text-foreground">
                  Recupero Password
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Contatta l'amministratore per reimpostare la password
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 mb-3">
                    <strong>Procedura di recupero password:</strong>
                  </p>
                  <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                    <li>Contatta l'amministratore di sistema</li>
                    <li>Comunica il tuo indirizzo email aziendale</li>
                    <li>L'amministratore resettera la password</li>
                    <li>Riceverai le nuove credenziali via email</li>
                  </ol>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Contatto amministratore:</p>
                  <p className="text-sm text-foreground font-medium">
                    amministrazione@splendorsud.it
                  </p>
                </div>
                <Button
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                >
                  Ho capito
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <p className="text-center text-white text-sm mt-6 bg-black/50 py-2 px-4 rounded-lg inline-block w-full">
          Splendor Sud S.r.l. - dal 1970
        </p>
      </div>
    </div>
  );
}
